import api from './api';

class DashboardService {
  // Get dashboard statistics
  async getStats(params = {}) {
    try {
      const response = await api.get('/dashboard/stats', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get revenue analytics - using system stats for now
  async getRevenueAnalytics(params = {}) {
    try {
      const response = await api.get('/system/stats', { params });
      const statsData = response.data;
      
      // Generate sample chart data based on stats
      const chartData = [];
      const today = new Date();
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        chartData.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          revenue: Math.floor(Math.random() * 5000) + 1000 // Will be replaced with real data
        });
      }
      
      return {
        chartData,
        totalRevenue: statsData.totalRevenue || 0,
        averageDaily: statsData.averageDaily || 0
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get booking trends - using dashboard stats
  async getBookingTrends(params = {}) {
    try {
      const response = await api.get('/dashboard/stats', { params });
      const statsData = response.data;
      
      // Convert booking counts to trends data
      const trends = [
        { name: 'Pending', count: statsData.pendingBookings || 0 },
        { name: 'Confirmed', count: statsData.confirmedBookings || 0 },
        { name: 'Completed', count: statsData.completedBookings || 0 },
        { name: 'Cancelled', count: statsData.cancelledBookings || 0 }
      ].filter(trend => trend.count > 0);
      
      return { trends };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get popular packages - using packages endpoint
  async getPopularPackages(params = {}) {
    try {
      const response = await api.get('/packages', { 
        params: { 
          ...params, 
          sort: 'booking_count', 
          order: 'desc',
          limit: params.limit || 5 
        } 
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get recent activities - mock data for now until backend implements this
  async getRecentActivities(params = {}) {
    try {
      // For now, return some sample activities based on recent bookings and users
      const [bookingsResponse, usersResponse] = await Promise.all([
        api.get('/bookings', { params: { limit: 5, sort: 'created_at', order: 'desc' } }),
        api.get('/users', { params: { limit: 5, sort: 'created_at', order: 'desc' } })
      ]);
      
      const activities = [];
      
      // Add recent bookings as activities
      if (bookingsResponse.data.bookings) {
        bookingsResponse.data.bookings.forEach(booking => {
          activities.push({
            type: 'booking',
            description: `New booking #${booking.id} for ${booking.package_name || 'Package'}`,
            timestamp: booking.created_at,
            user: booking.user_name || 'Guest'
          });
        });
      }
      
      // Add recent users as activities
      if (usersResponse.data.users) {
        usersResponse.data.users.slice(0, 3).forEach(user => {
          activities.push({
            type: 'user',
            description: `New user registered: ${user.name || user.email}`,
            timestamp: user.created_at,
            user: user.name || user.email
          });
        });
      }
      
      // Sort by timestamp
      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      return { activities: activities.slice(0, params.limit || 10) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Handle API errors
  handleError(error) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    } else if (error.message) {
      return new Error(error.message);
    } else {
      return new Error('An unexpected error occurred');
    }
  }
}

export default new DashboardService();